import { safeParseJson } from '@/utils/safeParseJson';
import { v1, toResult } from '@/infrastructure/api/v1/client';
import { listAll } from '@/infrastructure/api/v1/lists';
import { storyPatchBody } from '@/infrastructure/api/v1/mappers/stories';

/**
 * Marker used to delimit the auto-managed attribution block at the end of
 * scene_origin_description. Everything after this marker is owned by Lureo;
 * everything before it is the user's own description text.
 */
export const ATTRIBUTION_MARKER = '\n\n[ATTRIBUTIONS]\n';

/**
 * Split a full scene_origin_description into the user-written portion and the
 * array of individual attribution strings managed by Lureo.
 */
export function splitDescription(fullText: string): {
  userText: string;
  attributions: string[];
} {
  const idx = fullText.indexOf(ATTRIBUTION_MARKER);
  if (idx === -1) {
    return { userText: fullText, attributions: [] };
  }
  const userText = fullText.slice(0, idx);
  const block = fullText.slice(idx + ATTRIBUTION_MARKER.length);
  const attributions = block
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  return { userText, attributions };
}

/**
 * Combine the user-written text and attribution list back into the full
 * scene_origin_description string.
 */
export function joinDescription(userText: string, attributions: string[]): string {
  if (attributions.length === 0) return userText;
  return `${userText}${ATTRIBUTION_MARKER}${attributions.join('\n')}`;
}

// ---------------------------------------------------------------------------
// Database helpers
// ---------------------------------------------------------------------------

async function getSceneOriginId(sceneInstanceId: string): Promise<string | null> {
  const { data, error } = await toResult(v1.GET('/api/v1/scenes/{sceneUniqueId}', { params: { path: { sceneUniqueId: sceneInstanceId } } }));
  if (error) {
    if (error.status !== 404) console.warn('[Attribution] Failed to fetch scene_origin_id:', error);
    return null;
  }
  return data.storyUniqueId ?? null;
}

// Null when the story cannot be read: nothing is written from a description that was not read
async function getSceneOriginDescription(sceneOriginId: string): Promise<string | null> {
  const { data, error } = await toResult(v1.GET('/api/v1/stories/{storyUniqueId}', { params: { path: { storyUniqueId: sceneOriginId } } }));
  if (error) {
    if (error.status !== 404) console.warn('[Attribution] Failed to fetch scene_origin_description:', error);
    return null;
  }
  return data.description ?? '';
}

async function saveDescription(sceneOriginId: string, fullText: string): Promise<boolean> {
  const { error } = await toResult(v1.PATCH('/api/v1/stories/{storyUniqueId}', {
    params: { path: { storyUniqueId: sceneOriginId } },
    body: storyPatchBody({ scene_origin_description: fullText }),
  }));
  if (error) {
    console.warn('[Attribution] Failed to save scene_origin_description:', error);
    return false;
  }
  return true;
}

// Null when the credits already match; only the block after the marker changes
export function reconcileDescription(current: string, derived: string[], complete: boolean): string | null {
  const { userText, attributions } = splitDescription(current);
  const retained = complete ? attributions.filter((a) => derived.includes(a)) : attributions;
  const ordered = [...retained, ...derived.filter((a) => !retained.includes(a))];
  const unchanged = ordered.length === attributions.length && ordered.every((a, i) => a === attributions[i]);
  return unchanged ? null : joinDescription(userText, ordered);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Add an attribution string to the scene's description.
 * Appended to the managed [ATTRIBUTIONS] block at the end.
 * Silently no-ops if attribution is already present.
 */
export async function addAttributionToStory(sceneInstanceId: string, attribution: string): Promise<void> {
  const sceneOriginId = await getSceneOriginId(sceneInstanceId);
  if (!sceneOriginId) return;

  const current = await getSceneOriginDescription(sceneOriginId);
  if (current === null) return;
  const { userText, attributions } = splitDescription(current);

  if (attributions.includes(attribution)) return; // already present

  const updated = joinDescription(userText, [...attributions, attribution]);
  await saveDescription(sceneOriginId, updated);
  console.log('[Attribution] Added to scene description:', attribution);
}

/**
 * Pick the version whose assets the story's credits should reflect: the live
 * version if there is one, else the pending version, else the draft the user is
 * most likely to publish next (most recently edited). Attribution is stored once
 * per origin while assets live per version, so exactly one version governs.
 */
// Undefined when the versions cannot be read; null when the story has no version to derive from
async function getGoverningSceneInstanceId(sceneOriginId: string): Promise<string | null | undefined> {
  const { data, error } = await listAll(page =>
    v1.GET('/api/v1/scenes', { params: { query: { storyUniqueId: [sceneOriginId], ...page } } }),
  );
  if (error) {
    console.warn('[Attribution] Failed to fetch scene versions:', error);
    return undefined;
  }

  type VersionRow = {
    scene_instance_id: string;
    scene_instance_status: string | null;
    scene_instance_version_number: number | null;
    updated_at: string | null;
  };
  const rows: VersionRow[] = data.map(scene => ({
    scene_instance_id: scene.uniqueId,
    scene_instance_status: scene.status,
    scene_instance_version_number: scene.versionNumber,
    updated_at: scene.updatedAt,
  }));
  if (rows.length === 0) return null;

  const byVersionDesc = (a: VersionRow, b: VersionRow) =>
    (b.scene_instance_version_number ?? 0) - (a.scene_instance_version_number ?? 0);

  const withStatus = (status: string) => rows.filter((r) => r.scene_instance_status === status).sort(byVersionDesc);

  const live = withStatus('live')[0];
  if (live) return live.scene_instance_id;

  const pending = withStatus('pending')[0];
  if (pending) return pending.scene_instance_id;

  const drafts = rows
    .filter((r) => r.scene_instance_status === 'draft' || r.scene_instance_status === 'template')
    .sort((a, b) => {
      const byEdited = Date.parse(b.updated_at ?? '') - Date.parse(a.updated_at ?? '');
      return Number.isNaN(byEdited) || byEdited === 0 ? byVersionDesc(a, b) : byEdited;
    });

  return drafts[0]?.scene_instance_id ?? null;
}

/**
 * The attributions a version's assets require, distinct by asset type. `complete`
 * is false when an asset carries external-source metadata whose attribution is no
 * longer a readable string, which happens when asset_type_details gets clobbered
 * on the way through conversion. Credits cannot be safely removed in that case,
 * because the model may well still require one.
 */
async function deriveAttributions(sceneInstanceId: string): Promise<{ attributions: string[]; complete: boolean } | null> {
  // The asset types the version's assets use, in one read (listSceneAssetTypes); deleting a type takes its placements, so each is there
  const { data: types, error: typesError } = await listAll(page =>
    v1.GET('/api/v1/scenes/{sceneUniqueId}/asset-types', { params: { path: { sceneUniqueId: sceneInstanceId }, query: page } }),
  );
  if (typesError) {
    console.warn('[Attribution] Failed to fetch the asset types of the scene:', typesError);
    return null;
  }

  const rows = types.map(type => ({ asset_type_details: type.details }));
  const attributions: string[] = [];
  let complete = true;
  for (const row of rows) {
    const raw = row.asset_type_details;
    const details = safeParseJson<Record<string, unknown>>(raw as string);
    if (!details && raw != null) {
      complete = false;
      continue;
    }
    const rawAttribution = details?.attribution;
    const attribution = typeof rawAttribution === 'string' ? rawAttribution.trim() : '';
    if (attribution) {
      if (!attributions.includes(attribution)) attributions.push(attribution);
      continue;
    }
    const claimsExternalSource = !!details && ('attribution' in details || 'external_source' in details);
    if (claimsExternalSource) complete = false;
  }
  return { attributions, complete };
}

/**
 * Rewrite the story's attribution block from the governing version's assets.
 * The stored description is the source of truth (mobile renders the column raw),
 * so this writes through, and only when the string actually changes. Returns the
 * stored full description, or null if any read failed (nothing is written then).
 * With no version to derive from, the existing block is left alone rather than wiped.
 */
export async function reconcileStoryAttributions(sceneOriginId: string): Promise<string | null> {
  const sceneInstanceId = await getGoverningSceneInstanceId(sceneOriginId);
  if (sceneInstanceId === undefined) return null;
  const current = await getSceneOriginDescription(sceneOriginId);
  if (current === null) return null;
  if (!sceneInstanceId) return current;

  const derived = await deriveAttributions(sceneInstanceId);
  if (!derived) return null;

  const updated = reconcileDescription(current, derived.attributions, derived.complete);
  if (updated === null) return current;

  if (!(await saveDescription(sceneOriginId, updated))) return current;
  console.log('[Attribution] Reconciled story credits:', splitDescription(updated).attributions);
  return updated;
}

/**
 * Reconcile from any version of the story, including one that does not govern.
 */
export async function reconcileStoryAttributionsForScene(sceneInstanceId: string): Promise<string | null> {
  const sceneOriginId = await getSceneOriginId(sceneInstanceId);
  if (!sceneOriginId) return null;
  return reconcileStoryAttributions(sceneOriginId);
}
